/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import { ResponsiveBar } from "@nivo/bar";
import React, { useEffect, useState } from "react";
import fetchHelper from "../../services/fetchHelper";
import { useToasts } from "react-toast-notifications";

const colors = [
  "#9CA4F0",
  "#EF81B0",
  "#FF8E8E",
  "#FAD77F",
  "#63B58E",
  "#6ABED0",
  "#5AA3CD",
  "#F29fd6",
  "#Ef81b0",
  "#ffc1c1",
  "#FF8E8E",
  "#F8E9B7",
  "#FAD77F",
  "#96DFBC",
  "#64B696",
  "#B6E670",
  "#6ABED0",
  "#84D6E7",
  "#5AA3CD",
].reverse();

const Text = ({ children, y }) => {
  const id = Math.random().toString(36).substring(7);

  return (
    <text
      id={id}
      x={0}
      y={y}
      fontWeight="bold"
      textAnchor="middle"
      dominantBaseline="middle"
      style={{
        fill: "#333",
        fontSize: 10,
      }}
    >
      {children}
    </text>
  );
};

const Graph = ({ endDate: toDate, startDate: fromDate, where, onLoadingChange }) => {
  const { addToast } = useToasts();
  const [keys, setKeys] = useState(["UN (username1)", "UN (username2)"]);
  const [totalPerKey, setTotalPerKey] = useState({
    "UN (username1)": 2,
    "UN (username2)": 2,
    Week07: 4,
    Week10: 4,
  });
  const [rData, setRData] = useState([
    {
      week: "Week07",
      "UN (username2)": 1,
      "UN (username1)": 1,
    },
    {
      week: "Week10",
      "UN (username1)": 1,
      "UN (username2)": 1,
    },
  ]);

  const updateData = (data) => {
    const totalPerKey = {};
    const k = [];
    const rData = [];
    data.map((item) => {
      const r = {};
      r.week = item.week;
      const counts = item.counts || [];
      counts.map((count) => {
        const key = count.initials + " (" + count.username + ")";
        totalPerKey[key] = totalPerKey[key] || 0;
        totalPerKey[key] += count.count;
        totalPerKey[r.week] = totalPerKey[r.week] || 0;
        totalPerKey[r.week] += count.count;
        r[key] = count.count;
        if (k.indexOf(key) < 0) {
          k.push(key);
        }
      });
      rData.push(r);
    });
    setRData(rData);
    setKeys(k);
    setTotalPerKey(totalPerKey);
  };

  const queryDateFormat = (date) => {
    let year = date.getFullYear();
    let month = (1 + date.getMonth()).toString().padStart(2, "0");
    let day = date.getDate().toString().padStart(2, "0");

    return year + "-" + month + "-" + day;
  };

  const fetchData = async () => {
    onLoadingChange(true);
    if (!where) return false;
    const urlParams = {
      where: where,
    };
    if (fromDate) urlParams.fromDate = queryDateFormat(fromDate);
    if (toDate) urlParams.toDate = queryDateFormat(toDate);

    const urlQuery = new URLSearchParams(urlParams);
    let url = "/patientmanagement/rest/interrogations/assignerViewReport?" + urlQuery;
    let data;
    try {
      data = await fetchHelper.get({ url });
      data = data
        .map((data) => {
          const weekSplit = (data.week || "").split("-");
          return {
            ...data,
            week: `Week${weekSplit[weekSplit.length - 1]}`,
            counts: (data.counts || []).map((count) => {
              return {
                ...count,
                user_id: count.assignerId,
                count: count.notRemoved + count.removed,
              };
            }),
          };
        })
        .reverse();
    } catch (err) {
      err.errors.forEach((err) => {
        addToast(err.msg, {
          appearance: "error",
          autoDismiss: false,
        });
      });
      data = [];
    }
    updateData(data);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    onLoadingChange(false);
  };

  useEffect(() => {
    if (where) fetchData();
  }, [toDate, fromDate, where]);

  const CustomTick = (tick) => {
    return (
      <g transform={`translate(${tick.x},${tick.y + 22})`}>
        <Text>{tick.value}</Text>
        <Text y={10}>({totalPerKey[tick.value]})</Text>
      </g>
    );
  };

  return (
    <div style={{ width: "100%", height: 750, padding: 10, position: "relative" }}>
      <div
        style={{
          backgroundColor: "#fff",
          width: "100%",
          height: "100%",
          border: "1px solid #ddd",
          borderRadius: 2,
          position: "relative",
        }}
      >
        <ResponsiveBar
          data={rData}
          keys={keys}
          indexBy="week"
          margin={{ top: 50, right: 195, bottom: 80, left: 75 }}
          padding={0.3}
          colors={(data) => {
            return colors[keys.indexOf(data.id) % colors.length];
          }}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "#38bcb2",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "#eed312",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          fill={[
            {
              match: {
                id: "fries",
              },
              id: "dots",
            },
            {
              match: {
                id: "sandwich",
              },
              id: "lines",
            },
          ]}
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisTop={{
            tickSize: 0,
            format: () => null,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Weekly Reports",
            legendPosition: "middle",
            legendOffset: -30,
          }}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Week",
            legendPosition: "middle",
            legendOffset: 50,
            format: (data) => {
              return data + " (" + totalPerKey[data] + ")";
            },
            renderTick: CustomTick,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Total Reports",
            legendPosition: "middle",
            legendOffset: -60,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
        />
        <div style={{ position: "absolute", right: 10, bottom: 30, width: 215 }}>
          <ul style={{ fontSize: 12, listStyle: "none" }}>
            {keys.map((key, index) => {
              return (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: colors[index % colors.length],
                      marginRight: 3,
                    }}
                  />
                  <span>
                    {key} ({totalPerKey[key]})
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Graph;
